import { createRef, useEffect, useMemo, useRef, useState } from "react"

import { EyeIcon, EyeSlashIcon, HeartIcon, NoSymbolIcon, ShareIcon } from "@heroicons/react/24/outline"
import { groupBy, isEqual, partition, snakeCase, sortBy } from "lodash-es"
import qs from "qs"
import {
  Form,
  Link,
  Outlet,
  ScrollRestoration,
  useLoaderData,
  useLocation,
  useNavigate,
  useRevalidator,
  useSearchParams,
  useSubmit,
} from "react-router-dom"
import { twMerge } from "tailwind-merge"
import { slugify } from "underscore.string"

import { hasPanelData, LookbookDetailPanels, LookbookDetailPanelsData } from "./LookbookDetailPanels"
import { Bee } from "~/src/components/BeeKit"
import { FilterMap, filterMapCount, FilterValues, Filter as LookbookFilter } from "~/src/components/Dashboard/Filter"
import { FilterToggle } from "~/src/components/Dashboard/FilterToggle"
import { ItemGrid } from "~/src/components/Dashboard/ItemGrid"
import { ProductTile } from "~/src/components/Dashboard/ProductTile"
import { DropdownMenu, FullScreenMenu, SortOption, SortOptions } from "~/src/components/Dashboard/SortMenu"
import { Image } from "~/src/components/Image"
import { MainHeading } from "~/src/components/MainHeading"
import { Tab as GroupingTab, CategoryTabBar as GroupingTabBar } from "~/src/components/ProductSelection/CategoryTabBar"
import { iClient } from "~/src/lib/appClients"
import { iname } from "~/src/lib/iname"
import { dataFlag } from "~/src/lib/jsx"
import { Range } from "~/src/types/filter"
import { Photo } from "~/src/types/photo"

const qName = iname().q

const options: SortOptions = {
  default: "Newest",
  options: [
    { label: "Newest", field: "created_at", direction: "desc" },
    { label: "Oldest", field: "created_at", direction: "asc" },
    { label: "Price: Low to High", field: "low_price", direction: "asc" },
    { label: "Price: High to Low", field: "low_price", direction: "desc" },
    { label: "Order Minimum: Low to High", field: "minimum_quantity", direction: "asc" },
    { label: "Order Minimum: High to Low", field: "minimum_quantity", direction: "desc" },
  ],
}

const DEFAULT_MINIMUM_QUANTITIES: Range[] = [
  { label: "< 10", min: 1, max: 10 },
  { min: 11, max: 50 },
  { min: 51, max: 100 },
  { min: 101, max: 150 },
  { min: 151, max: 500 },
  { min: 501, max: 1000 },
  { label: "1001+", min: 1001, max: 1000000 },
]

export type ProductLookbookItem = {
  available: boolean
  productId: number
  ideabookProductId: number
  ideabookProductUrl: string
  ideabookGrouping: IdeabookGrouping
  slug: string
  name: string
  brand: string
  lowPrice: number
  loved: boolean
  hidden: boolean
  photo: Photo
  productPath: string
}

type IdeabookGrouping = {
  id: number
  name?: string
  position: number
}

type Lookbook = {
  id: string
  name: string
  description: string
}

//region Types
export type DashboardLookbooksShowProps = {
  loggedIn: boolean
  lookbook: Lookbook
  categories: string[]
  productFeatures: string[]
  minPrice: number
  maxPrice: number
  items: ProductLookbookItem[]
  panelData: LookbookDetailPanelsData
  coverImageUrl?: string
}
//endregion

//region Component
export function DashboardLookbooksShow() {
  const data = useLoaderData() as DashboardLookbooksShowProps

  const revalidator = useRevalidator()
  const [searchParams] = useSearchParams()
  const submit = useSubmit()
  const navigate = useNavigate()
  const location = useLocation()
  const tabInputRef = useRef<HTMLInputElement>(null)
  const [searchValue, setSearchValue] = useState<string>(searchParams.get("search") ?? "")
  const [initialGroupIndex, setInitialGroupIndex] = useState<number | null>(null)
  const [defaultFilters, sortValue] = useMemo(() => selectedFiltersFromSearchParams(searchParams), [searchParams])
  const [showDetails, setShowDetails] = useState<boolean>(false)

  useEffect(() => {
    if (searchValue != searchParams.get("search")) {
      setSearchValue(searchParams.get("search") ?? "")
    }
  }, [searchParams])

  const {
    lookbook,
    items,
    categories,
    productFeatures,
    panelData,
    coverImageUrl,
    minPrice = 0,
    maxPrice = 1000,
    loggedIn = true,
  } = data

  const scrollRoot = useRef<HTMLDivElement>(null)
  const orderedItemGroups = useMemo(() => {
    const [namedGroupItems, unnamedGroupItems] = partition(items, (item: ProductLookbookItem) => {
      const name = item.ideabookGrouping.name
      return name && name.trim().length > 0 && name != "Other"
    })
    const groups = Object.values(groupBy(namedGroupItems, (item: ProductLookbookItem) => item.ideabookGrouping.id))
    const sortedGroups = sortBy(groups, ([firstItem]) => firstItem.ideabookGrouping.position)
    if (unnamedGroupItems.length > 0) {
      const unnamedGroup = unnamedGroupItems.map((item) => {
        const ideabookGrouping = { ...item.ideabookGrouping, ...{ name: "Other" } }
        return { ...item, ...{ ideabookGrouping } }
      })
      sortedGroups.push(unnamedGroup)
    }

    return sortedGroups as (ProductLookbookItem & { ideabookGrouping: Required<IdeabookGrouping> })[][]
  }, [items])

  const groupingTabs = useMemo(
    () =>
      orderedItemGroups.map(([firstItem]) => {
        const label = firstItem.ideabookGrouping.name
        return {
          label,
          ref: createRef<HTMLDivElement>(),
          onClick: () => {
            navigate({ search: searchParams.toString(), hash: slugify(label) }, { preventScrollReset: true })
          },
        }
      }),
    [orderedItemGroups, navigate, searchParams]
  )

  useEffect(() => {
    if (initialGroupIndex == null && location.hash) {
      const index = groupingTabs.findIndex(({ label }) => `#${slugify(label)}` == location.hash)
      if (index > -1) {
        setInitialGroupIndex(index)
      }
    }
  }, [groupingTabs])

  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false)
  const emptyFilters = {
    categories: [],
    productFeatures: [],
    minimumQuantities: [],
    priceRange: { min: minPrice, max: maxPrice },
  }

  const initialFilters = useMemo(
    () => ({
      ...emptyFilters,
      ...defaultFilters,
    }),
    [defaultFilters]
  )

  const [selectedFilters, setSelectedFilters] = useState<FilterMap>(initialFilters)

  const filterDisabled = useMemo(() => isEqual(selectedFilters, initialFilters), [selectedFilters, initialFilters])

  const formRef = useRef<HTMLFormElement>(null)

  const selectedFiltersLength = useMemo(() => filterMapCount(selectedFilters), [selectedFilters])

  const selectedFilterNames = useMemo(
    () => filterNamesFromSelections(selectedFilters, minPrice, maxPrice),
    [selectedFilters]
  )

  const showDetailPanels = loggedIn && !coverImageUrl && hasPanelData(panelData)

  const ToggleIcon = showDetails ? EyeSlashIcon : EyeIcon
  const toggleText = showDetails ? "Hide details" : "Show details"
  const ToggleElement = !coverImageUrl ? (
    <div
      className="flex items-center gap-2 text-lg whitespace-nowrap text-gray-500 hover:cursor-pointer sm:text-blue-600"
      onClick={() => setShowDetails(!showDetails)}
    >
      <ToggleIcon className="h-6 w-6" />
      <span>{toggleText}</span>
    </div>
  ) : null

  const onSelect = (option: SortOption) => {
    if (!formRef.current) return
    const formData = new FormData(formRef.current)
    const { field, direction } = option

    formData.set(qName.sort[0]._, field)
    formData.set(qName.sort[1]._, direction)

    submit(formData)
  }

  //region Event Handlers
  const handleSectionChange = (section: string, newValues: FilterValues) => {
    setSelectedFilters((oldValues) => {
      return {
        ...oldValues,
        ...{ [section]: newValues },
      }
    })
  }

  const handleClearAll = () => {
    setSelectedFilters(emptyFilters)
    requestAnimationFrame(() => submit(formRef.current))
  }

  const handleLoveProduct = async (item: ProductLookbookItem) => {
    await iClient.put(
      `/ideabook_groupings/${item.ideabookGrouping.id}/product_preferences/${item.productId}/toggle/love`
    )

    // Forces a refresh of loader data.
    revalidator.revalidate()
  }

  const handleHideProduct = async (item: ProductLookbookItem) => {
    await iClient.put(
      `/ideabook_groupings/${item.ideabookGrouping.id}/product_preferences/${item.productId}/toggle/hidden`
    )

    // Forces a refresh of loader data.
    revalidator.revalidate()
  }

  const handleOnTabClick = (key: string) => () => {
    if (!tabInputRef.current) return

    tabInputRef.current.value = key

    if (!formRef.current) return

    const formData = new FormData(formRef.current)
    formData.delete("search")

    submit(formData)
  }
  //endregion

  //region JSX
  return (
    <>
      {coverImageUrl && (
        <Image
          src={coverImageUrl}
          alt="Cover Image"
          className="w-full sm:mt-8 sm:px-6"
          fallbackClassName="h-full w-full"
        >
          <></>
        </Image>
      )}

      <MainHeading
        title={lookbook.name}
        description={lookbook.description}
        className={twMerge(!loggedIn && "min-[1155px]:w-[1155px] min-[1155px]:place-self-center")}
        actions={
          loggedIn && (
            <>
              {showDetailPanels && ToggleElement}

              <div className="flex gap-4 text-lg text-blue-600">
                <Link to="share" className="flex flex-nowrap items-center gap-2">
                  <ShareIcon className="h-4 w-4" />
                  Share
                </Link>
              </div>
            </>
          )
        }
      />
      {showDetailPanels && <LookbookDetailPanels {...panelData} showDetails={showDetails} />}

      <hr className="border-gray-200" />

      <div
        className={twMerge(
          "mx-6 flex flex-col pb-3",
          !loggedIn && "mb-6 min-[1155px]:w-[1155px] min-[1155px]:place-self-center"
        )}
      >
        <ScrollRestoration
          getKey={(location) => [location.pathname, location.search, location.hash].filter(Boolean).join("")}
        />

        <Form ref={formRef} method="get" className="contents">
          <Bee.Searchbar
            name="search"
            value={searchValue}
            onChange={(event) => setSearchValue(event.currentTarget.value)}
            onClear={() => {
              setSearchValue("")

              if (!formRef.current) return

              const formData = new FormData(formRef.current)
              formData.delete("search")

              submit(formData)
            }}
            className="mt-8 w-full"
          />

          <Bee.HiddenInput name="tab" ref={tabInputRef} defaultValue={searchParams.get("tab") ?? "all_products"} />

          {selectedFilterNames.map(({ key, name, value }) => (
            <Bee.HiddenInput key={key} value={value} name={name} />
          ))}
        </Form>

        {loggedIn && (
          <Bee.TabBar className="mt-2">
            {[
              ["All Products"],
              ["Favorites", HeartIcon, "h-4 w-4 data-selected:text-magenta-500 data-selected:fill-current"],
              ["Hidden", NoSymbolIcon, "h-4 w-4"],
            ].map(([label, Icon, className]: [string, typeof HeartIcon, string]) => {
              const key = snakeCase(label)
              const isSelected = (searchParams.get("tab") ?? "all_products") === key

              return (
                <Bee.Tab type="button" key={key} onClick={handleOnTabClick(key)} selected={isSelected}>
                  <div className="flex items-center gap-1">
                    {Icon && <Icon className={className} {...dataFlag(isSelected, "selected")} />}
                    {isSelected ? `${label} (${items.length})` : label}
                  </div>
                </Bee.Tab>
              )
            })}
          </Bee.TabBar>
        )}

        <div className="h-full">
          <div className="mt-8 flex w-full items-center justify-between">
            <FilterToggle
              className="pb-2"
              mobileClassName={twMerge(
                "fixed right-1/2 bottom-4 z-30 h-10 w-20 -translate-x-px",
                selectedFiltersLength > 0 && "px-12"
              )}
              open={isFilterOpen}
              numSelectedFilters={selectedFiltersLength}
              onClearAll={handleClearAll}
              onToggleFilter={() => setIsFilterOpen(!isFilterOpen)}
            />
            {/* Desktop Experience */}
            <DropdownMenu className="hidden sm:flex" value={sortValue} sortOptions={options} onSelect={onSelect} />
            {/* Mobile Experience */}
            <FullScreenMenu
              value={sortValue}
              className="sm:hidden"
              buttonClassName="fixed bottom-4 left-1/2 translate-x-px z-10 h-10 w-20 rounded-l-none"
              menuClassName="fixed inset-0 z-50"
              sortOptions={options}
              onSelect={onSelect}
            />
          </div>

          <div className="flex w-full">
            <LookbookFilter
              disabled={filterDisabled}
              open={isFilterOpen}
              selectedItemMap={selectedFilters}
              onSectionChange={handleSectionChange}
              onApply={() => submit(formRef.current)}
              onClearAll={handleClearAll}
              onClose={() => isFilterOpen && setIsFilterOpen(false)}
            >
              <LookbookFilter.StringSection title="Categories" sectionKey="categories" items={categories} />
              <LookbookFilter.StringSection
                title="Product Features"
                sectionKey="productFeatures"
                items={productFeatures}
              />
              <LookbookFilter.RangeSection
                title="Minimum Quantity"
                sectionKey="minimumQuantities"
                items={DEFAULT_MINIMUM_QUANTITIES}
              />
              <LookbookFilter.SliderSection
                title="Lowest Unit Price"
                sectionKey="priceRange"
                bounds={{ min: minPrice, max: maxPrice }}
              />
            </LookbookFilter>
            {items.length > 0 && (searchParams.get("tab") == null || searchParams.get("tab") == "all_products") ? (
              <ItemGridGrouped
                loggedIn={loggedIn}
                lookbookId={lookbook.id}
                groupingTabs={groupingTabs}
                itemGroups={orderedItemGroups}
                initialGroupIndex={initialGroupIndex}
                scrollRoot={scrollRoot}
                onLove={handleLoveProduct}
                onHide={handleHideProduct}
              />
            ) : (
              <ItemGridUngrouped
                loggedIn={loggedIn}
                lookbookId={lookbook.id}
                items={items}
                onLove={handleLoveProduct}
                onHide={handleHideProduct}
              />
            )}
          </div>
        </div>
        <Outlet />
      </div>
    </>
  )
  //endregion
}

//endregion

//region Private
function filterNamesFromSelections(
  selectedFilters: FilterMap,
  minPrice: number,
  maxPrice: number
): { key: string; name: string; value: string }[] {
  return Object.keys(selectedFilters)
    .flatMap((key) => {
      const sectionValues = selectedFilters[key]
      switch (key) {
        case "priceRange": {
          const rangeValue = sectionValues as Range
          if (rangeValue.min === minPrice && rangeValue.max === maxPrice) {
            return []
          } else {
            return [
              ["pr", "min", rangeValue.min.toString()],
              ["pr", "max", rangeValue.max.toString()],
            ]
          }
        }
        case "minimumQuantities": {
          const rangeValues = sectionValues as Range[]
          return rangeValues.flatMap((value: Range, index: number) => [
            ["mqs", index, "min", value.min.toString()],
            ["mqs", index, "max", value.max.toString()],
          ])
        }
        case "categories": {
          const categoryValues = sectionValues as string[]
          return categoryValues.map((value: string, index: number) => ["cs", index, value])
        }
        case "productFeatures": {
          const productFeatureValues = sectionValues as string[]
          return productFeatureValues.map((value: string, index: number) => ["pfs", index, value])
        }
      }
    })
    .map((pieces: string[]) => ({
      key: pieces.join("."),
      name: pieces
        .slice(0, -1)
        .reduce((acc, piece) => acc[piece], qName)
        .toString(),
      value: pieces.at(-1),
    }))
    .filter((piece) => piece.value != null) as { key: string; name: string; value: string }[]
}

type LookbookSearchParams = {
  q?: {
    /** Maps to `categories` */
    cs?: string[]
    /** Maps to `productFeatures` */
    pfs?: string[]
    /** Maps to `minimumQuantities` */
    mqs?: { min: string; max: string }[]
    /** Maps to `priceRange` */
    pr?: { min: string; max: string }
    /** Maps to `sort` */
    sort?: [string, string]
  }
}

/**
 * Parses the given search parameters and returns the default selected filters.
 *
 * @param searchParams - The URLSearchParams object containing the search parameters.
 * @returns The default selected filters as an object of type FilterValues.
 */
function selectedFiltersFromSearchParams(searchParams: URLSearchParams): [FilterMap, SortOption | undefined] {
  const parsedParams = qs.parse(searchParams.toString(), { ignoreQueryPrefix: true }) ?? {}
  const { q: { cs, pfs, mqs, pr, sort } = {} }: LookbookSearchParams = parsedParams

  const filters = {}
  if (Array.isArray(cs)) {
    filters["categories"] = Array.from(new Set(cs.filter((x) => typeof x === "string")))
  }

  if (Array.isArray(pfs)) {
    filters["productFeatures"] = Array.from(new Set(pfs.filter((x) => typeof x === "string")))
  }

  if (Array.isArray(mqs)) {
    const minimumQuantities = mqs
      .filter((x) => typeof x === "object")
      .map((range: { min: string; max: string }) => {
        const min = parseInt(range.min)
        const max = parseInt(range.max)

        if (min > 0 && max > min) {
          return { min, max }
        }
      })
      .filter((x) => x != null)

    if (minimumQuantities.length > 0) {
      filters["minimumQuantities"] = minimumQuantities
    }
  }

  if (
    // 🤦
    typeof pr === "object" &&
    "min" in pr &&
    "max" in pr &&
    typeof pr.min === "string" &&
    typeof pr.max === "string"
  ) {
    filters["priceRange"] = {
      min: parseInt(pr.min),
      max: parseInt(pr.max),
    }
  }

  let sortOption: SortOption | undefined

  if (Array.isArray(sort)) {
    const [field, direction] = sort
    sortOption = options.options.find((option) => option.field === field && option.direction === direction)
  }
  return [filters, sortOption]
}

type ItemGridGroupedProps = {
  loggedIn: boolean
  lookbookId: string
  groupingTabs: GroupingTab[]
  itemGroups: ProductLookbookItem[][]
  initialGroupIndex?: number | null
  scrollRoot: React.RefObject<HTMLDivElement | null>
  onLove?: (item: ProductLookbookItem) => void
  onHide?: (item: ProductLookbookItem) => void
}

function ItemGridGrouped(props: ItemGridGroupedProps) {
  const { loggedIn, lookbookId, groupingTabs, initialGroupIndex, itemGroups, scrollRoot, onLove, onHide } = props

  return (
    <div className="flex w-full flex-col gap-6">
      <GroupingTabBar
        tabs={groupingTabs}
        scrollRoot={scrollRoot}
        scrollOffset={120}
        kind="secondary"
        containerClassName="bg-white px-2 py-4 top-[3.4rem] bg-gray-50 z-30"
        className="gap-0"
        {...(initialGroupIndex != null ? { initialIndex: initialGroupIndex } : {})}
      />
      {itemGroups.map((itemGroup, index) => {
        const groupId = itemGroup[0].ideabookGrouping.id
        const groupName = itemGroup[0].ideabookGrouping.name
        return (
          <div key={groupId} ref={groupingTabs[index].ref} className="flex scroll-mt-[120px] flex-col gap-6">
            <h4 className="p-2 text-2xl font-medium">{groupName}</h4>
            <ItemGrid
              items={itemGroup}
              renderItem={(item: ProductLookbookItem) => {
                const { ideabookProductId, productId, ...rest } = item
                return (
                  <div key={ideabookProductId} className="flex items-center justify-center p-1">
                    <ProductTile
                      id={productId}
                      loggedIn={loggedIn}
                      baseProductPath={`lookbooks/${lookbookId}`}
                      {...(loggedIn ? { onLove: () => onLove?.(item), onHide: () => onHide?.(item) } : {})}
                      {...rest}
                    />
                  </div>
                )
              }}
            />
          </div>
        )
      })}
    </div>
  )
}

type ItemGridUngroupedProps = {
  loggedIn: boolean
  lookbookId: string
  items: ProductLookbookItem[]
  onLove?: (item: ProductLookbookItem) => void
  onHide?: (item: ProductLookbookItem) => void
}

function ItemGridUngrouped(props: ItemGridUngroupedProps) {
  const { loggedIn, lookbookId, items, onLove, onHide } = props

  return (
    <div className="flex w-full flex-col gap-6">
      <ItemGrid
        items={items}
        renderItem={(item: ProductLookbookItem) => {
          const { ideabookProductId, productId, ...rest } = item
          return (
            <div key={ideabookProductId} className="flex items-center justify-center p-1">
              <ProductTile
                id={productId}
                loggedIn={loggedIn}
                baseProductPath={`lookbooks/${lookbookId}`}
                {...(loggedIn ? { onLove: () => onLove?.(item), onHide: () => onHide?.(item) } : {})}
                {...rest}
              />
            </div>
          )
        }}
      />
    </div>
  )
}

//endregion
